import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAppTheme } from '../App';
import { COLORS } from '../common/colors';
import { AnalyticsData } from '../services/analyticsService';

interface AnalyticsCardsProps {
    analyticsData: AnalyticsData | null;
    loading: boolean;
    position: 'left' | 'right';
}

const AnalyticsCards: React.FC<AnalyticsCardsProps> = ({ analyticsData, loading, position }) => {
    const { getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();

    const formatNumber = (num: number): string => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    if (position === 'left') {
        // Left side - only user count
        return (
            <Box sx={{ 
                position: 'absolute',
                left: { lg: '-15%', xl: '-40%' },
                top: '20%',
                zIndex: 10,
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                    '0%, 100%': {
                        transform: 'translateY(0px)',
                    },
                    '50%': {
                        transform: 'translateY(-20px)',
                    },
                },
            }}>
                <Card
                    sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.06) 100%)`,
                        boxShadow: `0 8px 32px rgba(76, 175, 80, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)`,
                        backdropFilter: 'blur(16px)',
                        border: `2px solid rgba(76, 175, 80, 0.3)`,
                        transition: 'all 0.3s ease',
                        width: 280,
                        '&:hover': {
                            transform: 'translateY(-10px) scale(1.05)',
                            boxShadow: `0 12px 40px rgba(76, 175, 80, 0.4), 0 6px 20px rgba(0, 0, 0, 0.3)`,
                        },
                    }}
                    elevation={0}
                >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: '#4CAF50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                            }}
                        >
                            <PeopleIcon sx={{ fontSize: 28, color: 'white' }} />
                        </Box>
                        
                        {loading ? (
                            <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto', mb: 1 }} />
                        ) : (
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    mb: 1,
                                    fontSize: '2rem',
                                }}
                            >
                                {formatNumber(analyticsData?.numberOfUsers || 0)}
                            </Typography>
                        )}
                        
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.textPrimary,
                                mb: 0.5,
                                fontSize: '1rem',
                            }}
                        >
                            Active Users
                        </Typography>
                        
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.textSecondary,
                                opacity: 0.8,
                                fontSize: '0.75rem',
                            }}
                        >
                            Trusted worldwide
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Right side - personal statement and profile score
    return (
        <Box sx={{ 
            position: 'absolute',
            right: { lg: '-15%', xl: '-40%' },
            top: '15%',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
        }}>
            {/* Personal Statement Card */}
            <Box sx={{
                animation: 'float 6s ease-in-out infinite',
                animationDelay: '0s',
                '@keyframes float': {
                    '0%, 100%': {
                        transform: 'translateY(0px)',
                    },
                    '50%': {
                        transform: 'translateY(-15px)',
                    },
                },
            }}>
                <Card
                    sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, rgba(254, 106, 46, 0.12) 0%, rgba(254, 106, 46, 0.06) 100%)`,
                        boxShadow: `0 8px 32px rgba(254, 106, 46, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)`,
                        backdropFilter: 'blur(16px)',
                        border: `2px solid rgba(254, 106, 46, 0.3)`,
                        transition: 'all 0.3s ease',
                        width: 280,
                        '&:hover': {
                            transform: 'translateY(-8px) scale(1.05)',
                            boxShadow: `0 12px 40px rgba(254, 106, 46, 0.4), 0 6px 20px rgba(0, 0, 0, 0.3)`,
                        },
                    }}
                    elevation={0}
                >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                backgroundColor: COLORS.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                boxShadow: '0 6px 20px rgba(254, 106, 46, 0.4)',
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Box>
                        
                        {loading ? (
                            <Skeleton variant="text" width="70%" height={28} sx={{ mx: 'auto', mb: 1 }} />
                        ) : (
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    mb: 1,
                                    fontSize: '1.5rem',
                                }}
                            >
                                {formatNumber(analyticsData?.personalStatementGenerated || 0)}
                            </Typography>
                        )}
                        
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: theme.textPrimary,
                                mb: 0.5,
                                fontSize: '0.9rem',
                            }}
                        >
                            Personal Statements
                        </Typography>
                        
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.textSecondary,
                                opacity: 0.8,
                                fontSize: '0.7rem',
                            }}
                        >
                            Successfully generated
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Profile Score Card */}
            <Box sx={{
                animation: 'float 6s ease-in-out infinite',
                animationDelay: '2s',
                '@keyframes float': {
                    '0%, 100%': {
                        transform: 'translateY(0px)',
                    },
                    '50%': {
                        transform: 'translateY(-15px)',
                    },
                },
            }}>
                <Card
                    sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)`,
                        boxShadow: `0 8px 32px rgba(59, 130, 246, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)`,
                        backdropFilter: 'blur(16px)',
                        border: `2px solid rgba(59, 130, 246, 0.3)`,
                        transition: 'all 0.3s ease',
                        width: 280,
                        '&:hover': {
                            transform: 'translateY(-8px) scale(1.05)',
                            boxShadow: `0 12px 40px rgba(59, 130, 246, 0.4), 0 6px 20px rgba(0, 0, 0, 0.3)`,
                        },
                    }}
                    elevation={0}
                >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                backgroundColor: '#3B82F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                            }}
                        >
                            <AssessmentIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Box>
                        
                        {loading ? (
                            <Skeleton variant="text" width="70%" height={28} sx={{ mx: 'auto', mb: 1 }} />
                        ) : (
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    mb: 1,
                                    fontSize: '1.5rem',
                                }}
                            >
                                {formatNumber(analyticsData?.profileScored || 0)}
                            </Typography>
                        )}
                        
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: theme.textPrimary,
                                mb: 0.5,
                                fontSize: '0.9rem',
                            }}
                        >
                            Profile Analyses
                        </Typography>
                        
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.textSecondary,
                                opacity: 0.8,
                                fontSize: '0.7rem',
                            }}
                        >
                            Comprehensive evaluations
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default AnalyticsCards; 